<template>
    <div class="verification-code" :class="{ 'is-verified': is_verified }">
        <div v-if="!is_verified" class="verification-content">
            <div class="verification-header">
                <v-icon color="primary" size="20">mdi-shield-lock-outline</v-icon>
                <span class="verification-title">Verify your identity</span>
            </div>

            <p class="verification-message">
                We sent a 6-digit code to <strong>{{ phone_number }}</strong>
            </p>

            <div class="code-input-container">
                <input
                    v-for="(digit, index) in code_digits"
                    :key="index"
                    ref="codeInputs"
                    v-model="code_digits[index]"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="code-input"
                    :class="{ 'has-value': digit }"
                    @input="handleInput(index, $event)"
                    @keydown="handleKeydown(index, $event)"
                    @paste="handlePaste"
                    data-1p-ignore
                />
            </div>

            <button
                class="verify-button"
                :class="{ 'is-verifying': is_verifying }"
                :disabled="!is_code_complete || is_verifying"
                @click="handleVerify"
            >
                <span v-if="is_verifying" class="button-content">
                    <v-progress-circular
                        indeterminate
                        color="white"
                        :size="16"
                        :width="2"
                    />
                    <span>Verifying...</span>
                </span>
                <span v-else class="button-content">
                    Verify
                </span>
            </button>

            <button class="resend-link" @click="handleResend" :disabled="resend_cooldown > 0 || show_resend_confirmation">
                <template v-if="show_resend_confirmation">
                    <v-icon color="success" x-small>mdi-check</v-icon>
                    Code sent!
                </template>
                <template v-else-if="resend_cooldown > 0">
                    Resend code in {{ resend_cooldown }}s
                </template>
                <template v-else>
                    Didn't receive a code? Resend
                </template>
            </button>
        </div>

        <div v-else class="verified-content">
            <v-icon color="success" size="20">mdi-check-circle</v-icon>
            <span>Identity Verified</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockVerificationCode',

    props: {
        phone_number: {
            type: String,
            default: '(555) 987-6543'
        },
        verification_delay: {
            type: Number,
            default: 1200
        },
        auto_fill: {
            type: Boolean,
            default: false
        },
        auto_fill_code: {
            type: String,
            default: '847291'
        }
    },

    data() {
        return {
            code_digits: ['', '', '', '', '', ''],
            is_verifying: false,
            is_verified: false,
            resend_cooldown: 0,
            cooldown_interval: null,
            show_resend_confirmation: false
        };
    },

    computed: {
        is_code_complete() {
            return this.code_digits.every(digit => digit !== '');
        },

        full_code() {
            return this.code_digits.join('');
        }
    },

    mounted() {
        this.$nextTick(() => {
            if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
                this.$refs.codeInputs[0].focus();
            }

            if (this.auto_fill) {
                this.autoFillCode();
            }
        });
    },

    beforeDestroy() {
        if (this.cooldown_interval) {
            clearInterval(this.cooldown_interval);
        }
    },

    methods: {
        async autoFillCode() {
            await this.delay(500);

            const digits = this.auto_fill_code.split('');
            for (let i = 0; i < digits.length && i < 6; i++) {
                this.code_digits.splice(i, 1, digits[i]);
                this.$refs.codeInputs[i]?.focus();
                await this.delay(100);
            }
        },

        handleInput(index, event) {
            const value = event.target.value;

            // digits only
            if (value && !/^\d$/.test(value)) {
                this.code_digits.splice(index, 1, '');
                return;
            }

            // move to next
            if (value && index < 5) {
                this.$nextTick(() => {
                    this.$refs.codeInputs[index + 1].focus();
                });
            }
        },

        handleKeydown(index, event) {
            if (event.key === 'Backspace' && !this.code_digits[index] && index > 0) {
                this.$refs.codeInputs[index - 1].focus();
            }
            if (event.key === 'ArrowLeft' && index > 0) {
                this.$refs.codeInputs[index - 1].focus();
            }
            if (event.key === 'ArrowRight' && index < 5) {
                this.$refs.codeInputs[index + 1].focus();
            }
        },

        handlePaste(event) {
            event.preventDefault();
            const pasted_data = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

            pasted_data.split('').forEach((digit, index) => {
                if (index < 6) {
                    this.code_digits.splice(index, 1, digit);
                }
            });

            const last_index = Math.min(pasted_data.length, 5);
            this.$nextTick(() => {
                this.$refs.codeInputs[last_index].focus();
            });
        },

        handleVerify() {
            if (!this.is_code_complete || this.is_verifying) return;

            this.is_verifying = true;
            this.$emit('verifying');

            setTimeout(() => {
                this.is_verifying = false;
                this.is_verified = true;
                this.$emit('verified', { code: this.full_code });
            }, this.verification_delay);
        },

        handleResend() {
            if (this.resend_cooldown > 0 || this.show_resend_confirmation) return;

            this.show_resend_confirmation = true;
            this.$emit('resend');

            setTimeout(() => {
                this.show_resend_confirmation = false;
                this.resend_cooldown = 30;

                this.cooldown_interval = setInterval(() => {
                    this.resend_cooldown--;
                    if (this.resend_cooldown <= 0) {
                        clearInterval(this.cooldown_interval);
                    }
                }, 1000);
            }, 2000);
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        reset() {
            this.code_digits = ['', '', '', '', '', ''];
            this.is_verifying = false;
            this.is_verified = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.verification {
    &-code {
        width: 100%;
    }

    &-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        padding: 1.25rem;
        background: #fff;
        border-radius: 0.75rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    &-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #333;
    }

    &-message {
        font-size: 0.85rem;
        color: #666;
        text-align: center;
        margin: 0;
        line-height: 1.4;

        strong {
            color: #333;
        }
    }
}

.verify-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.75rem 1.5rem;
    margin-top: 0.25rem;
    background: var(--brand-primary);
    border: none;
    border-radius: 0.5rem;
    color: white;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: var(--brand-primary-dark);
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        background: #ccc;
    }

    &.is-verifying {
        background: var(--brand-primary);
    }

    .button-content {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: #fff;
    }
}

.verified-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;
    background: #e8f5e9;
    border-radius: 0.5rem;
    color: var(--v-success-darken1);
    font-weight: 600;
}

.code-input {
    width: 2.25rem;
    height: 2.75rem;
    border: 2px solid #ddd;
    border-radius: 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
    text-align: center;
    background: #fafafa;
    color: #333;
    transition: all 0.2s ease;

    &-container {
        display: flex;
        gap: 0.5rem;
        margin: 0.5rem 0;
    }

    &:focus {
        outline: none;
        border-color: var(--brand-primary);
        background: #fff;
    }

    &.has-value {
        border-color: var(--brand-primary);
        background: #fff;
    }
}

.resend-link {
    background: none;
    border: none;
    font-size: 0.8rem;
    color: var(--brand-primary);
    cursor: pointer;
    padding: 0.25rem;
    transition: color 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;

    &:hover:not(:disabled) {
        color: var(--brand-primary-dark);
        text-decoration: underline;
    }

    &:disabled {
        color: #999;
        cursor: default;
        text-decoration: none;
    }
}
</style>
